// src/prototype/components/ActivationChecklist.tsx
//
// Stateful when onToggle is supplied: the boxes are real checkboxes and the "N of M complete"
// line derives from what is ticked. Previously the boxes rendered checkbox iconography with no
// handler — honest by the letter (no hover, no cursor) but it invited a click that did nothing.
// Without onToggle it still renders read-only, so non-editable callers are unaffected.
import React from 'react';
import { Check } from 'lucide-react';
import type { ChecklistItem } from '../data/executionInitiatives';

export interface ActivationChecklistProps {
  items: ChecklistItem[];
  /** Supply to make the list editable. */
  onToggle?: (label: string) => void;
}

export const ActivationChecklist: React.FC<ActivationChecklistProps> = ({ items, onToggle }) => {
  // Derived, never read from the fixture — the count and the boxes cannot disagree.
  const done = items.filter((i) => i.done).length;

  const Box: React.FC<{ item: ChecklistItem }> = ({ item }) => (
    <span
      className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-colors ${
        item.done ? 'bg-emerald-500 text-white' : 'bg-white border border-[#cbd5e1]'
      }`}
    >
      {item.done && <Check className="w-3 h-3" />}
    </span>
  );

  return (
    <>
      <ul className="space-y-2" data-testid="activation-checklist">
        {items.map((item) => (
          <li key={item.label} className="flex items-center gap-2.5 text-xs">
            {onToggle ? (
              <button
                type="button"
                role="checkbox"
                aria-checked={item.done}
                onClick={() => onToggle(item.label)}
                className="flex items-center gap-2.5 text-left w-full group/check rounded hover:bg-[#F5F7FA] -mx-1 px-1 py-0.5 transition-colors"
              >
                <Box item={item} />
                <span className={item.done ? 'text-[#4b5563]' : 'text-[#717182]'}>{item.label}</span>
              </button>
            ) : (
              <>
                <Box item={item} />
                <span className={item.done ? 'text-[#4b5563]' : 'text-[#717182]'}>{item.label}</span>
              </>
            )}
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-[#717182] mt-3" data-testid="checklist-count">
        {done} of {items.length} complete
      </p>
    </>
  );
};

export default ActivationChecklist;
